/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import UniversalTracker from '../../../model/UniversalTracker';
import { GlossaryState, TableDataInfo, ValueData, ValueDataObject } from './questionInterfaces';
import { isDisabled } from '../form/input/table/tableUtils';
import { findAll } from 'highlight-words-core';
import SimpleTooltip from '../../simple-tooltip';
import { nl2br } from '../../../utils';
import { ValueHistory } from '../../../types/universalTrackerValue';
import { getGroup, standards } from '@g17eco/core';

export interface DataCheck {
  utr: UniversalTracker | undefined;
  displayCheckbox: { [p: string]: boolean };
  valueData: ValueData;
  value: number | undefined;
  table: TableDataInfo | undefined;
}

export const isDataEmpty = ({ utr, displayCheckbox, valueData, value, table }: DataCheck) => {
  if (!utr) {
    return true;
  }

  switch (utr.getValueType()) {
    case 'text':
    case 'date':
    case 'valueList':
      return !valueData.data || typeof valueData.data !== 'string';
    case 'valueListMulti':
      return !valueData.data || typeof valueData.data !== 'object' || !Array.isArray(valueData.data);
    case 'numericValueList': {
      if (!valueData.data) {
        return true;
      }
      const d = valueData.data as ValueDataObject;
      // Selected
      const keys: string[] = Object.keys(displayCheckbox).filter((k) => displayCheckbox[k]);
      // It's empty checkbox or there is at least one empty value
      return keys.length === 0 || keys.some((k: string) => isNaN(d[k] as number));
    }
    case 'textValueList': {
      if (!valueData.data) {
        return true;
      }
      const d = valueData.data as ValueDataObject; // { lll: 'yes what ever' }
      // Selected
      const keys: string[] = Object.keys(displayCheckbox).filter((k) => displayCheckbox[k]);
      // It's empty checkbox or there is at least one empty value
      return keys.length === 0 || keys.some((k: string) => !d[k]);
    }
    case 'number':
    case 'percentage':
      return value === undefined || isNaN(value);
    case 'table': {
      if (table) {
        // new table format
        if (!Array.isArray(table.rows) || table.rows.filter((r) => !r.isRemoved).length === 0) {
          return true;
        }
        const valueValidation = utr.getValueValidation();
        if (valueValidation.table?.columns) {
          const cols = valueValidation.table.columns;
          return table.rows.some((row) => isDisabled(cols, row.data));
        }
        if (valueValidation.table?.columns) {
          const cols = valueValidation.table.columns;
          return table.rows.some((row) => isDisabled(cols, row.data));
        }
      }
      return !Array.isArray(valueData.table) || valueData.table.length === 0;
    }
    default:
      return true;
  }
};

export const getTooltip = (textToHighlight: string, glossaryState: GlossaryState) => {
  const searchWords = Object.keys(glossaryState ?? {});

  const chunks = findAll({
    caseSensitive: false,
    searchWords,
    textToHighlight,
  });

  return chunks.map((chunk, i) => {
    const { end, highlight, start } = chunk;
    const text = textToHighlight.substr(start, end - start);
    if (!highlight) {
      return nl2br(text);
    }

    const definitionText = (
      <div
        dangerouslySetInnerHTML={{ __html: glossaryState ? glossaryState[text.toLowerCase()]?.html : '' }}
        className='text-left'
      />
    );
    return (
      <SimpleTooltip text={definitionText} key={i}>
        <abbr title=''>{text} </abbr>
      </SimpleTooltip>
    );
  });
};

export const checkHasComments = (
  stakeholderHistory: Pick<ValueHistory, 'note'>,
  verifierHistory: Pick<ValueHistory, 'note'>
) => {
  const combinedHistory = [stakeholderHistory, verifierHistory];
  return combinedHistory.some((c) => Boolean(c?.note));
};

/**
 * Retrieves the latest alternative code for a framework.
 */

export const getLatestAlternativeCode = ({ utr }: { utr: UniversalTracker }) => {
  let currentStandard = getGroup('standards', utr.getType());
  const versionGroupCode = currentStandard?.versionGroupCode;
  if (!currentStandard || !versionGroupCode) {
    return utr.getType();
  }

  for (const standard of Object.values(standards)) {
    if (
      standard.versionGroupCode === versionGroupCode &&
      Number(standard.version) > Number(currentStandard.version) &&
      utr.hasAlternativeInfo(standard.code)
    ) {
      currentStandard = standard;
    }
  }

  return currentStandard.code;
};
